import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon as IconContainer } from '@picsio/ui';
import { AiEngine } from '@picsio/icons';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';

import useTeammatesNavigate from '../../helpers/components/hooks/useTeammatesNavigate';
import { selectAccessLevel } from '../../store/selectors/teammates';
import { setIsVisualSearchBannerState } from '../../store/actions/main';
import { LocalStorage } from '../../shared/utils';
import localization from '../../shared/strings';
import Logger from '../../services/Logger';
import { navigate } from '../../helpers/history';
import { showDialog } from '../dialog';
import Icon from '../Icon';
import styles from './styles.module.scss';

const VisualSearchBanner = ({ bannerText, className, actionText }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const accessLevel = useSelector(selectAccessLevel);
  const visualSearch = _get(user, 'subscriptionFeatures.visualSearch');
  const { adminEmails, onTeammateNavigateClick } = useTeammatesNavigate('tab=ai');

  const onSendToIntercom = useCallback(async () => {
    Logger.log('User', 'VisualSearchRequestModalProceedToChatClicked', {
      bannerLocation: actionText ? 'content_area' : 'autosuggest',
    });
    await window.Intercom('showNewMessage');
    setTimeout(() => {
      window.Intercom('private:sendMessage', localization.DIALOGS.VISUAL_SEARCH.SUPPORT_MESSAGE);
    }, 2000);
  }, [actionText]);

  const adminRolesEmails = useMemo(() => (accessLevel === 'noAccess' ? adminEmails : ''), [accessLevel, adminEmails]);

  const onEnableVisualSearch = useCallback(() => {
    navigate('/teammates?tab=ai');
  }, []);

  const onCancel = useCallback(() => {
    Logger.log('User', 'VisualSearchRequestModalCancelClicked', {
      bannerLocation: actionText ? 'content_area' : 'autosuggest',
    });
  }, [actionText]);

  const accessDialogConfigMap = useMemo(() => ({
    fullAccess: {
      textBtnCancel: localization.DIALOGS.VISUAL_SEARCH.CANCEL_TEXT,
      textBtnOk: localization.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.ENABLE_VISUAL_SEARCH,
      additionalTextBtn: localization.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.PROCEED_TO_CHAT,
      onAdditionalClick: onSendToIntercom,
      onOk: onEnableVisualSearch,
      onCancel,
      onClose: onCancel,
    },
    partialAccess: {
      textBtnCancel: localization.DIALOGS.VISUAL_SEARCH.CANCEL_TEXT,
      textBtnOk: localization.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.PROCEED_TO_CHAT,
      onOk: onSendToIntercom,
      onCancel,
      onClose: onCancel,
    },
    noAccess: {
      textBtnCancel: null,
      textBtnOk: localization.DIALOGS.VISUAL_SEARCH.OK_BUTTON_TEXTS.OK,
      onClose: onCancel,
    },
  }), [onCancel, onEnableVisualSearch, onSendToIntercom]);

  const accessDescriptionsMap = useMemo(() => ({
    fullAccess: localization.DIALOGS.VISUAL_SEARCH.PERMISSION_TEXTS.FULL_ACCESS,
    partialAccess: localization.DIALOGS.VISUAL_SEARCH.PERMISSION_TEXTS.PARTIAL_ACCESS,
    noAccess: localization.DIALOGS.VISUAL_SEARCH.PERMISSION_TEXTS.NO_ACCESS,
  }), []);

  const onShowDialog = useCallback(() => {
    Logger.log('User', 'VisualSearchRequestModalOpened', {
      bannerLocation: actionText ? 'content_area' : 'autosuggest',
    });

    showDialog({
      title: localization.DIALOGS.VISUAL_SEARCH.TITLE,
      text: localization.DIALOGS.VISUAL_SEARCH.TEXT(accessDescriptionsMap[accessLevel], adminRolesEmails),
      ...accessDialogConfigMap[accessLevel],
    });
  }, [actionText, accessDescriptionsMap, accessLevel, adminRolesEmails, accessDialogConfigMap]);

  const bannerTypeText = visualSearch ? localization.VISUAL_SEARCH_BANNER.ENABLE : localization.VISUAL_SEARCH_BANNER.REQUEST;

  const onOpenVisualSearchModal = useCallback(() => {
    if (actionText) return;

    Logger.log('User', 'VisualSearchBannerClicked', {
      bannerType: bannerTypeText,
      bannerLocation: 'autosuggest',
    });

    if (visualSearch) {
      onTeammateNavigateClick();
      return;
    }

    onShowDialog();
  }, [actionText, bannerTypeText, onTeammateNavigateClick, onShowDialog, visualSearch]);

  const onKeyDown = useCallback((event) => {
    const KEY_ENTER = 13;
    const { keyCode, shiftKey } = event;

    if (keyCode === KEY_ENTER && !shiftKey) {
      onShowDialog();
    }
  }, [onShowDialog]);

  const onActionTextClick = useCallback((event) => {
    event.stopPropagation();

    Logger.log('User', 'VisualSearchBannerClicked', {
      bannerType: actionText,
      bannerLocation: 'content_area',
    });

    if (visualSearch) {
      onTeammateNavigateClick();
      return;
    }

    onShowDialog();
  }, [actionText, onTeammateNavigateClick, onShowDialog, visualSearch]);

  const onHideBanner = useCallback((e) => {
    e.stopPropagation();

    Logger.log('User', 'VisualSearchBannerClosed', {
      bannerType: bannerTypeText,
      bannerLocation: actionText ? 'content_area' : 'autosuggest',
    });

    LocalStorage.set('visualSearchBannerHidden', true);
    dispatch(setIsVisualSearchBannerState(true));
  }, [actionText, bannerTypeText, dispatch]);

  return (
    <div role="button" tabIndex={0} className={cn(className, styles.banner)} onClick={onOpenVisualSearchModal} onKeyDown={onKeyDown}>
      <IconContainer size="lg" className={styles.bannerIcon}>
        <AiEngine />
      </IconContainer>
      <div className={styles.bannerText}>{bannerText}</div>
      {actionText && (
        <Button
          type="text"
          className={styles.bannerActionText}
          onKeyDown={onKeyDown}
          onClick={onActionTextClick}
        >
          {actionText}
        </Button>
      )}
      <Icon name="close" className={styles.bannerClose} onClick={onHideBanner} />
    </div>
  );
};

VisualSearchBanner.propTypes = {
  actionText: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.string]),
  bannerText: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.string]).isRequired,
  className: PropTypes.string,
};

VisualSearchBanner.defaultProps = {
  actionText: '',
  className: '',
};

export default VisualSearchBanner;
